import { useDocumentVisibility, useIntervalFn } from '@vueuse/core';
import { computed, ref, type Ref, watch } from 'vue';
import { api } from '../../../shared/api/client.ts';
import type { ErrorSlug, GameView } from '../../../shared/api/game.ts';
import { failedOnTheServer, meetsStaleView } from '../../../shared/api/serverFailure.ts';

const HEARTBEAT_INTERVAL_MS = 15_000;
const gameAbandonedSlug = 'game-abandoned' satisfies ErrorSlug;

export function useHeartbeats(
  phase: Ref<GameView['phase'] | undefined>,
  streamStatus: Ref<string>,
  refuse: (message: string) => void = () => {},
) {
  const visibility = useDocumentVisibility();
  const gameAbandoned = ref(false);
  const sendHeartbeat = async () => {
    const { error } = await api.POST('/game/heartbeat').catch(() => ({ error: undefined }));
    if (error?.slug === gameAbandonedSlug) gameAbandoned.value = true;
    else if (error && !failedOnTheServer(error) && !meetsStaleView(error.slug)) refuse(error.message);
  };
  const { pause, resume } = useIntervalFn(sendHeartbeat, HEARTBEAT_INTERVAL_MS, { immediate: false });
  const heartbeatsDue = computed(
    () => visibility.value === 'visible' && phase.value !== undefined && phase.value !== 'GAME_OVER',
  );
  watch(heartbeatsDue, (due) => (due ? resume() : pause()), { immediate: true });
  watch(streamStatus, (status) => {
    if (status === 'OPEN' && heartbeatsDue.value) void sendHeartbeat();
  });

  return { gameAbandoned };
}
